'use client';
import React, { useEffect, useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface Ticket {
  id: string;
  ticket_number: string;
  subject: string;
  category: string;
  ticket_status: string;
  priority: string;
  order_number: string | null;
  admin_reply: string | null;
  resolution_note: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: string }> = {
  open:               { label: 'Open',               cls: 'bg-green-100 text-green-700',  icon: 'ChatBubbleLeftRightIcon' },
  in_progress:        { label: 'In Progress',        cls: 'bg-blue-100 text-blue-700',    icon: 'ArrowPathIcon' },
  awaiting_customer:  { label: 'Awaiting You',       cls: 'bg-amber-100 text-amber-700',  icon: 'ClockIcon' },
  resolved:           { label: 'Resolved',           cls: 'bg-purple-100 text-purple-700', icon: 'CheckCircleIcon' },
  closed:             { label: 'Closed',             cls: 'bg-slate-100 text-slate-600',  icon: 'ArchiveBoxIcon' },
};

const PRIORITY_CONFIG: Record<string, { label: string; cls: string }> = {
  low:    { label: 'Low',    cls: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Medium', cls: 'bg-blue-100 text-blue-600' },
  high:   { label: 'High',   cls: 'bg-orange-100 text-orange-600' },
  urgent: { label: 'Urgent', cls: 'bg-red-100 text-red-600' },
};

const CATEGORIES = [
  { value: 'order_issue',       label: 'Order Issue' },
  { value: 'return_dispute',    label: 'Return / Dispute' },
  { value: 'payment_issue',     label: 'Payment Issue' },
  { value: 'delivery_issue',    label: 'Delivery Issue' },
  { value: 'product_complaint', label: 'Product Complaint' },
  { value: 'seller_complaint',  label: 'Seller Complaint' },
  { value: 'account_issue',     label: 'Account Issue' },
  { value: 'other',             label: 'Other' },
];

const EMPTY_TICKET = {
  subject: '',
  category: 'order_issue',
  priority: 'medium',
  order_number: '',
  message: '',
};

export default function CustomerSupportPage() {
  const router = useRouter();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [newTicket, setNewTicket] = useState(EMPTY_TICKET);
  const [creating, setCreating] = useState(false);
  const [userId, setUserId] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchTickets = useCallback(async (uid: string) => {
    const { data } = await supabase
      .from('customer_support_tickets')
      .select('id, ticket_number, subject, category, ticket_status, priority, order_number, admin_reply, resolution_note, resolved_at, created_at, updated_at')
      .eq('user_id', uid)
      .order('created_at', { ascending: false });
    setTickets(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      fetchTickets(user.id);
    });
  }, [router, fetchTickets]);

  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.message) {
      showToast('Please fill in subject and message.', 'error');
      return;
    }
    setCreating(true);
    try {
      const ticketNumber = `TKT-${Date.now().toString().slice(-8)}`;
      const { error } = await supabase.from('customer_support_tickets').insert({
        user_id: userId,
        ticket_number: ticketNumber,
        subject: newTicket.subject,
        category: newTicket.category,
        priority: newTicket.priority,
        order_number: newTicket.order_number || null,
        ticket_status: 'open',
        ticket_source: 'customer',
        internal_notes: newTicket.message,
      });
      if (error) throw error;
      showToast('Support ticket created successfully!', 'success');
      setShowForm(false);
      setNewTicket(EMPTY_TICKET);
      fetchTickets(userId);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create ticket.';
      showToast(message, 'error');
    } finally {
      setCreating(false);
    }
  };

  const filtered = filterStatus === 'all'
    ? tickets
    : tickets.filter(t => t.ticket_status === filterStatus);

  const openCount = tickets.filter(t => ['open', 'in_progress', 'awaiting_customer'].includes(t.ticket_status)).length;

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-700 text-slate-900">Support</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {openCount > 0 ? `${openCount} active ticket${openCount > 1 ? 's' : ''}` : 'Get help from our support team'}
            </p>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary px-4 py-2.5 rounded-xl text-sm font-600 flex items-center gap-2"
          >
            <Icon name="PlusIcon" size={16} /> New Ticket
          </button>
        </div>

        {/* New Ticket Form */}
        {showForm && (
          <div className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
            <h2 className="text-base font-700 text-slate-900">Create Support Ticket</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Subject <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={newTicket.subject}
                  onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))}
                  placeholder="Briefly describe your issue"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Category</label>
                <select
                  value={newTicket.category}
                  onChange={e => setNewTicket(t => ({ ...t, category: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Priority</label>
                <select
                  value={newTicket.priority}
                  onChange={e => setNewTicket(t => ({ ...t, priority: e.target.value }))}
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                >
                  {['low', 'medium', 'high', 'urgent'].map(p => (
                    <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Related Order Number</label>
                <input
                  type="text"
                  value={newTicket.order_number}
                  onChange={e => setNewTicket(t => ({ ...t, order_number: e.target.value }))}
                  placeholder="e.g. ORD-123456 (optional)"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-600 text-slate-500 mb-1.5">Message <span className="text-red-500">*</span></label>
                <textarea
                  value={newTicket.message}
                  onChange={e => setNewTicket(t => ({ ...t, message: e.target.value }))}
                  rows={4}
                  placeholder="Describe your issue in detail…"
                  className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleCreateTicket}
                disabled={creating}
                className="btn-primary px-6 py-2.5 rounded-xl text-sm font-600 disabled:opacity-60 flex items-center gap-2"
              >
                {creating ? <><Icon name="ArrowPathIcon" size={14} className="animate-spin" /> Creating…</> : 'Submit Ticket'}
              </button>
              <button
                onClick={() => { setShowForm(false); setNewTicket(EMPTY_TICKET); }}
                className="px-6 py-2.5 rounded-xl text-sm font-600 border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1">
          {['all', 'open', 'in_progress', 'awaiting_customer', 'resolved', 'closed'].map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-2 rounded-xl text-xs font-600 whitespace-nowrap transition-colors ${
                filterStatus === s ? 'bg-primary text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-primary/40'
              }`}
            >
              {s === 'all' ? 'All' : STATUS_CONFIG[s]?.label || s}
              {s !== 'all' && (
                <span className="ml-1 opacity-70">({tickets.filter(t => t.ticket_status === s).length})</span>
              )}
            </button>
          ))}
        </div>

        {/* Tickets list */}
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
              <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <Icon name="ChatBubbleLeftRightIcon" size={24} className="text-slate-300" />
              </div>
              <p className="text-sm text-slate-400">No tickets found</p>
              <p className="text-xs text-slate-400 mt-1">Create a ticket to get help from our support team</p>
            </div>
          ) : (
            filtered.map(ticket => {
              const statusCfg = STATUS_CONFIG[ticket.ticket_status] || STATUS_CONFIG.open;
              const priorityCfg = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
              const categoryLabel = CATEGORIES.find(c => c.value === ticket.category)?.label || ticket.category;
              const isExpanded = expanded === ticket.id;
              return (
                <div key={ticket.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div
                    className="px-5 py-4 flex items-center justify-between gap-4 cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => setExpanded(isExpanded ? null : ticket.id)}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${statusCfg.cls}`}>
                        <Icon name={statusCfg.icon as never} size={16} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-700 text-slate-900 truncate">{ticket.subject}</p>
                          <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${statusCfg.cls}`}>{statusCfg.label}</span>
                          <span className={`text-xs font-500 px-2 py-0.5 rounded-full ${priorityCfg.cls}`}>{priorityCfg.label}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5">
                          #{ticket.ticket_number} · {categoryLabel}
                          {ticket.order_number ? ` · Order #${ticket.order_number}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-400">
                        {new Date(ticket.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                      <Icon name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={16} className="text-slate-400" />
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-5 pb-5 border-t border-slate-100 pt-4 space-y-4">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <p className="text-xs text-slate-400">Ticket Number</p>
                          <p className="font-500 text-slate-800 mt-0.5">#{ticket.ticket_number}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Category</p>
                          <p className="font-500 text-slate-800 mt-0.5">{categoryLabel}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Priority</p>
                          <span className={`inline-flex text-xs font-500 px-2 py-0.5 rounded-full mt-0.5 ${priorityCfg.cls}`}>
                            {priorityCfg.label}
                          </span>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400">Status</p>
                          <span className={`inline-flex items-center gap-1 text-xs font-500 px-2 py-0.5 rounded-full mt-0.5 ${statusCfg.cls}`}>
                            <Icon name={statusCfg.icon as never} size={12} />
                            {statusCfg.label}
                          </span>
                        </div>
                        {ticket.resolved_at && (
                          <div>
                            <p className="text-xs text-slate-400">Resolved At</p>
                            <p className="font-500 text-slate-800 mt-0.5">
                              {new Date(ticket.resolved_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        )}
                      </div>

                      {ticket.admin_reply && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="ChatBubbleLeftRightIcon" size={14} className="text-blue-600" />
                            <p className="text-xs font-700 text-blue-700">Support Team Reply</p>
                          </div>
                          <p className="text-sm text-blue-800 leading-relaxed">{ticket.admin_reply}</p>
                        </div>
                      )}

                      {ticket.resolution_note && (
                        <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon name="CheckCircleIcon" size={14} className="text-green-600" />
                            <p className="text-xs font-700 text-green-700">Resolution Note</p>
                          </div>
                          <p className="text-sm text-green-800 leading-relaxed">{ticket.resolution_note}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </CustomerDashboardLayout>
  );
}
